import { Injectable } from '@angular/core';
import moment from 'moment';
import { DATE_ISO_FORMAT, DATE_TIME_FORMAT, DATE_TIME_FORMAT_12H, TIME_FORMAT, TIME_FORMAT_12H, WIDGET_TIME_FORMAT, WidgetTimeFormat } from '../../constants/date.constants';

@Injectable({
  providedIn: 'root'
})
export class DateUtilService {

  readonly dateIsoFormat = DATE_ISO_FORMAT;

  private _userTimeFormat: string;
  private _userDateTimeFormat: string;

  constructor() {
    this.detectUserTimeFormat();
  }

  /**
   * Returns the appropriate time format string for moment.js
   * @returns 'hh:mm A' for 12-hour format or 'HH:mm' for 24-hour format
   */
  get userTimeFormat(): string {
    if (this._userTimeFormat === null) {
      this.detectUserTimeFormat();
    }
    return this._userTimeFormat;
  }

  /**
   * Returns the appropriate date-time format string for moment.js
   * @returns Format with AM/PM for 12-hour or without for 24-hour
   */
  get userDateTimeFormat(): string {
    if (this._userDateTimeFormat === null) {
      this.detectUserTimeFormat();
    }
    return this._userDateTimeFormat;
  }

  /**
   * Sets the time format based on partner's widget_time_format setting
   * @param widgetTimeFormat - WIDGET_TIME_FORMAT.AUTO | WIDGET_TIME_FORMAT.HOURS_12 | WIDGET_TIME_FORMAT.HOURS_24
   */
  setTimeFormatFromPartner(widgetTimeFormat: WidgetTimeFormat): void {
    switch (widgetTimeFormat) {
      case WIDGET_TIME_FORMAT.HOURS_12:
        this._userTimeFormat = TIME_FORMAT_12H;
        this._userDateTimeFormat = DATE_TIME_FORMAT_12H;
        break;
      case WIDGET_TIME_FORMAT.HOURS_24:
        this._userTimeFormat = TIME_FORMAT;
        this._userDateTimeFormat = DATE_TIME_FORMAT;
        break;
      case WIDGET_TIME_FORMAT.AUTO:
      default:
        this.detectUserTimeFormat();
        break;
    }
  }

  /**
   * Uses Intl.DateTimeFormat to detect if the user's locale uses 12-hour time format
   * and caches the result
   */
  private detectUserTimeFormat(): void {
    try {
      const formattedTime = new Intl.DateTimeFormat(navigator.language || 'de-CH', {
        hour: "numeric",
        minute: "numeric"
      }).format(new Date(2025, 0, 1, 13, 0));


      // If the formatted string contains 'PM' or 'AM', it's 12-hour format
      // This works for most locales including en-US, en-GB, etc.
      const has12HourFormat = /am|pm|a\.m\.|p\.m\./i.test(formattedTime) || formattedTime.includes('午後') || formattedTime.includes('午前');;

      this._userTimeFormat = has12HourFormat ? TIME_FORMAT_12H : TIME_FORMAT;
      this._userDateTimeFormat = has12HourFormat ? DATE_TIME_FORMAT_12H : DATE_TIME_FORMAT;
    } catch (error) {
      this._userTimeFormat = TIME_FORMAT;
      this._userDateTimeFormat = DATE_TIME_FORMAT;
    }
  }

  getDateByPlaceholder(placeholder: string): string {
    switch (placeholder) {
      case '[TODAY]':
        return moment().format(this.dateIsoFormat);
      case '[TOMORROW]':
        return moment().add(1, 'd').format(this.dateIsoFormat);
      default:
        return placeholder;
    }
  }

  getDateFromString(date: string, format: string): Date {
    return moment(date, format).toDate();
  }

  getFormattedDateByFormat(date: Date, format: string): string {
    return moment(date).format(format);
  }

  getStartOf(date: Date, unit: any): Date {
    return moment(date).startOf(unit).toDate();
  }

  isSame(firstDate: Date, secondDate: Date, granularity?: any): boolean {
    if (granularity) {
      return moment(firstDate).isSame(secondDate, granularity);
    } else {
      return moment(firstDate).isSame(secondDate);
    }
  }
}
